import { useState, useEffect, useCallback } from "react";
import { api, errMsg, unitWord } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { SubmitTaskDialog, PhotoInput } from "../components/SubmitTaskDialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Star, Video } from "lucide-react";

const DIR_LABEL = { office_to_maintenance: "Office → Maintenance", maintenance_to_office: "Maintenance → Office", cross_division_bonus: "Cross-Division Bonus (+10)", support_team: "Support Team tasks (remote OK)" };
const DIRS = ["office_to_maintenance", "maintenance_to_office", "cross_division_bonus", "support_team"];

function CapacityBanner({ p, onSaved }) {
  const [capInput, setCapInput] = useState("");
  const save = async () => {
    try {
      const { data } = await api.put("/my/capacity", { capacity: parseInt(capInput, 10) });
      toast.success(`Capacity set — you're in the ${data.tier} tier and ready to compete!`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="border border-slate-200 border-l-4 border-l-[#c8102e] bg-[#f8f9fc] rounded-md p-4 mb-6" data-testid="needs-capacity-banner">
      <div className="font-semibold text-[#1a1f36] mb-1">Set your capacity to compete</div>
      <p className="text-sm text-slate-600 mb-2">Your property can't be ranked until bed count is set.{p.note ? ` Hint: ${p.note}.` : ""}</p>
      <div className="flex gap-2">
        <Input type="number" min="1" placeholder={`Number of ${p.capacity_type}`} value={capInput} onChange={(e) => setCapInput(e.target.value)} className="max-w-40 bg-white" data-testid="set-capacity-input" />
        <Button onClick={save} className="sw-red hover:bg-red-700 text-white" data-testid="set-capacity-btn">Save</Button>
      </div>
    </div>
  );
}

function StatsRow({ overview, p, isSupport }) {
  return (
    <div className="grid grid-cols-3 gap-3 mb-8 stagger">
      <div className="border border-slate-200 rounded-md p-4"><div className="text-2xl font-display font-bold text-[#1a1f36]" data-testid="stat-points">{overview.approved_points}</div><div className="text-xs text-slate-500 uppercase tracking-wide">{isSupport ? "Visit points" : "Approved points"}</div></div>
      <div className="border border-slate-200 rounded-md p-4"><div className="text-2xl font-display font-bold text-slate-600" data-testid="stat-pending">{overview.pending_count}</div><div className="text-xs text-slate-500 uppercase tracking-wide">Pending</div></div>
      {isSupport ? (
        <div className="border border-slate-200 rounded-md p-4"><div className="text-2xl font-display font-bold text-[#c8102e]" data-testid="stat-visits">{overview.support_visits ?? 0}</div><div className="text-xs text-slate-500 uppercase tracking-wide">Approved visits</div></div>
      ) : (
        <div className="border border-slate-200 rounded-md p-4"><div className="text-2xl font-display font-bold text-[#c8102e]" data-testid="stat-per-capacity">{overview.task_per_capacity?.toFixed(3) ?? "—"}</div><div className="text-xs text-slate-500 uppercase tracking-wide">Pts per {unitWord(p.capacity_type)}</div></div>
      )}
    </div>
  );
}

function TasksTab({ tasks, isSupport, onLog }) {
  const grouped = DIRS.map((d) => ({ dir: d, items: tasks.filter((t) => t.direction === d) }));
  return (
    <TabsContent value="tasks" className="space-y-6 mt-4">
      {isSupport && (
        <div className="border border-slate-200 bg-[#f8f9fc] rounded-md p-4 text-sm text-slate-700" data-testid="support-tasks-note">
          <span className="font-semibold text-[#1a1f36]">The visit is the scoring unit.</span> These are the real property task lists — show up onsite (or join by Teams) and complete one alongside the property team. Every approved visit earns <b>+15 for your floor</b> and automatically hands that property <b>+10 bonus points</b> (max 3 visits per property). Photo or call screenshot + the property supervisor's sign-off. One task per person, and the property never logs anything.
        </div>
      )}
      {grouped.map(({ dir, items }) => items.length > 0 && (
        <div key={dir}>
          <h3 className="font-display font-semibold text-[#1a1f36] mb-2">{isSupport ? `${DIR_LABEL[dir]} — visit a property` : DIR_LABEL[dir]}</h3>
          <div className="space-y-2">
            {items.map((t) => (
              <div key={t.id} className="border border-slate-200 rounded-md px-4 py-3 flex items-center justify-between gap-3 hover:shadow-sm transition-shadow" data-testid={`task-row-${t.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-[#1a1f36]">{t.name}</div>
                  <div className="text-xs text-slate-500">Sign-off: {isSupport ? "Property supervisor" : t.supervised_by}{t.completed_by.length > 0 && ` · done by ${t.completed_by.map((c) => c.person).join(", ")}`}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-display font-bold text-[#c8102e]">{isSupport ? 15 : t.points}</span>
                  <Button size="sm" variant="outline" onClick={() => onLog(t)} data-testid={`task-log-btn-${t.id}`}>Log it</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </TabsContent>
  );
}

function SubmissionsTab({ subs }) {
  return (
    <TabsContent value="submissions" className="mt-4 space-y-2">
      {subs.length === 0 && <p className="text-sm text-slate-500 py-8 text-center">Nothing submitted yet — knock out your first task!</p>}
      {subs.map((s) => (
        <div key={s.id} className="border border-slate-200 rounded-md px-4 py-3 flex items-center gap-3" data-testid={`submission-row-${s.id}`}>
          {s.photo && <img src={s.photo} alt="" className="w-12 h-12 rounded object-cover shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-[#1a1f36] truncate">{s.task_name}</div>
            <div className="text-xs text-slate-500">{s.person_name} ({s.person_role}){s.visited_property_name ? ` · visited ${s.visited_property_name}` : ""} · sign-off {s.supervisor} · {s.points} pts</div>
            {s.admin_note && <div className="text-xs text-red-600 mt-0.5">Note: {s.admin_note}</div>}
          </div>
          <StatusBadge status={s.status} />
        </div>
      ))}
    </TabsContent>
  );
}

function ReelTab({ overview, config, onSaved }) {
  const [reelUrl, setReelUrl] = useState(overview.reel?.video_url || "");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/my/reel", { video_url: reelUrl });
      toast.success("Reel saved!");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <TabsContent value="reel" className="mt-4">
      <div className="border border-slate-200 rounded-md p-5 max-w-lg">
        <h3 className="font-display font-semibold text-[#1a1f36] flex items-center gap-2 mb-1"><Video className="w-4 h-4 text-[#c8102e]" />Your reel</h3>
        {overview.reel ? (
          <div className="mb-3 space-y-1" data-testid="reel-status">
            {config && config.today > config.campaign_end ? (
              overview.reel.avg != null ? (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800" data-testid="reel-status-scored">Scored: {overview.reel.avg}/30</span>
              ) : (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800" data-testid="reel-status-judging">In judging — watch the leaderboard and Teams for the winners</span>
              )
            ) : (
              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800" data-testid="reel-status-submitted">Submitted ✓</span>
            )}
            <div className="text-sm text-slate-600">On file: <a href={overview.reel.video_url} target="_blank" rel="noreferrer" className="text-[#c8102e] hover:underline break-all" data-testid="reel-saved-link">{overview.reel.video_url}</a></div>
            {config && config.today <= config.campaign_end && <p className="text-xs text-slate-500">You can update the link anytime — even after the window closes.</p>}
          </div>
        ) : (
          <p className="text-sm text-slate-600 mb-3">Paste a link — YouTube, Google Drive, Instagram, TikTok all work. Judges score Teamwork, Creativity, and Teaches something real (each /10). Worth 30% of your final score.</p>
        )}
        <form onSubmit={save} className="flex gap-2">
          <Input value={reelUrl} onChange={(e) => setReelUrl(e.target.value)} placeholder="https://…" required data-testid="reel-url-input" />
          <Button type="submit" className="sw-red hover:bg-red-700 text-white" data-testid="reel-save-btn">{overview.reel ? "Update" : "Save"}</Button>
        </form>
        {overview.reel?.avg != null && <p className="text-sm mt-3 font-medium">Current judge average: <span className="text-[#c8102e]">{overview.reel.avg}/30</span></p>}
        {overview.reel && <p className="text-xs text-slate-500 mt-1">{overview.reel.votes} People's Choice votes</p>}
      </div>
    </TabsContent>
  );
}

function BonusTab({ bonus, onSaved }) {
  const [form, setForm] = useState({ photo: null, regional_name: "" });
  const submit = async () => {
    if (!form.photo || !form.regional_name) return toast.error("Regional name and photo required");
    try {
      await api.post("/my/commander-bonus", form);
      toast.success("Commander Bonus submitted for verification");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <TabsContent value="bonus" className="mt-4">
      <div className="border border-slate-200 rounded-md p-5 max-w-lg">
        <h3 className="font-display font-semibold text-[#1a1f36] flex items-center gap-2 mb-1"><Star className="w-4 h-4 text-[#c8102e]" />Commander Bonus (+15)</h3>
        <p className="text-sm text-slate-600 mb-3">When your Regional completes one task with your team, snap a photo. Once the Support Team verifies it, +15 raw points are added.</p>
        {bonus ? (
          <div className="flex items-center gap-2"><StatusBadge status={bonus.status} /><span className="text-sm text-slate-600">Submitted for {bonus.regional_name}</span></div>
        ) : (
          <div className="space-y-2">
            <Input placeholder="Regional's name" value={form.regional_name} onChange={(e) => setForm({ ...form, regional_name: e.target.value })} data-testid="bonus-regional-input" />
            <PhotoInput photo={form.photo} setPhoto={(ph) => setForm({ ...form, photo: ph })} testid="bonus-photo-input" />
            <Button onClick={submit} className="sw-red hover:bg-red-700 text-white" data-testid="bonus-submit-btn">Submit for verification</Button>
          </div>
        )}
      </div>
    </TabsContent>
  );
}

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [subs, setSubs] = useState([]);
  const [bonus, setBonus] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [config, setConfig] = useState(null);

  const load = useCallback(async () => {
    const [o, t, s, b, c] = await Promise.all([
      api.get("/my/property"), api.get("/my/tasks"), api.get("/my/submissions"),
      api.get("/my/commander-bonus"), api.get("/config"),
    ]);
    setOverview(o.data); setTasks(t.data); setSubs(s.data); setBonus(b.data?.id ? b.data : null);
    setConfig(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!overview) return <div className="p-12 text-center text-slate-500">Loading your property…</div>;
  const p = overview.property;
  const isSupport = p.division === "Support";

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 fade-up">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#c8102e]">{p.division} · {p.tier || "tier pending"} · {p.location}</p>
          <h1 className="font-display text-3xl font-bold text-[#1a1f36]">{p.name}</h1>
          {config && <p className="text-sm text-slate-500 mt-1">Competition window: {config.campaign_start} → {config.campaign_end} (everyone competes together)</p>}
        </div>
        <StatusBadge status={p.status} />
      </div>

      {p.status === "Needs capacity" && <CapacityBanner p={p} onSaved={load} />}

      <StatsRow overview={overview} p={p} isSupport={isSupport} />

      <Tabs defaultValue="tasks">
        <TabsList className="w-full flex overflow-x-auto justify-start">
          <TabsTrigger value="tasks" data-testid="tab-tasks">{isSupport ? "Visit tasks" : "Task list"}</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="tab-submissions">My submissions</TabsTrigger>
          {!isSupport && <TabsTrigger value="reel" data-testid="tab-reel">Reel</TabsTrigger>}
          {!isSupport && <TabsTrigger value="bonus" data-testid="tab-bonus">Commander</TabsTrigger>}
        </TabsList>

        <TasksTab tasks={tasks} isSupport={isSupport} onLog={setActiveTask} />
        <SubmissionsTab subs={subs} />
        {!isSupport && <ReelTab key={overview.reel?.video_url || "no-reel"} overview={overview} config={config} onSaved={load} />}
        {!isSupport && <BonusTab bonus={bonus} onSaved={load} />}
      </Tabs>

      {activeTask && <SubmitTaskDialog task={activeTask} division={p.division} onClose={() => setActiveTask(null)} onDone={() => { setActiveTask(null); load(); }} />}
    </div>
  );
}
